<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-height="680"
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    style="z-index: 1100 !important;"
  >
    <v-card v-cloak data-cy="dialog-clientCredentials-modal">
      <v-card-title class="d-flex justify-space-between" data-cy="dialog-clientCredentials-title">
        <span>
          <v-icon class="mr-2">mdi-check-circle</v-icon>
          {{ $t('routes.apiClients.clientCreatedTitle') }}
        </span>
        <v-btn icon variant="text" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-4">
        <div class="credentials-section mb-4">
          <label class="text-caption text-grey">{{ $t('routes.apiClients.clientId') }}</label>
          <div class="d-flex align-center">
            <code class="credential-value flex-grow-1">{{ client?.client_id }}</code>
            <v-btn
              icon
              variant="text"
              size="small"
              @click="copyToClipboard(client?.client_id, 'clientId')"
              data-cy="dialog-credentials-copyClientId"
            >
              <v-icon>{{ copiedField === 'clientId' ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
            </v-btn>
          </div>
        </div>

        <div class="credentials-section mb-4">
          <label class="text-caption text-grey">{{ $t('routes.apiClients.clientSecret') }}</label>
          <div class="d-flex align-center">
            <code class="credential-value flex-grow-1">{{ client?.client_secret }}</code>
            <v-btn
              icon
              variant="text"
              size="small"
              @click="copyToClipboard(client?.client_secret, 'clientSecret')"
              data-cy="dialog-credentials-copyClientSecret"
            >
              <v-icon>{{ copiedField === 'clientSecret' ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
            </v-btn>
          </div>
        </div>

        <div class="d-flex gap-2 mt-4">
          <v-btn
            variant="outlined"
            color="primary"
            @click="copyAllToClipboard"
            data-cy="dialog-credentials-copyAll"
          >
            <v-icon start>{{ copiedField === 'all' ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
            {{ $t('routes.apiClients.copyAll') }}
          </v-btn>

          <v-btn
            variant="outlined"
            color="primary"
            @click="downloadAsJson"
            data-cy="dialog-credentials-download"
          >
            <v-icon start>mdi-download</v-icon>
            {{ $t('routes.apiClients.downloadJson') }}
          </v-btn>
        </div>

        <v-alert type="warning" variant="tonal" class="mt-4">
          {{ $t('routes.apiClients.secretWarning') }}
        </v-alert>

      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          class="bg-primary"
          elevation="0"
          @click="closeDialog"
          data-cy="dialog-credentials-close"
        >
          {{ $t('common.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';

export default {
  mixins: [DialogActionKeys],
  name: "ClientCredentialsDialog",
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    client: {
      type: Object,
      default: null,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      copiedField: null,
    };
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        this.copiedField = null;
      }
    },
  },
  methods: {
    async copyToClipboard(text, fieldName) {
      try {
        await navigator.clipboard.writeText(text);
        this.copiedField = fieldName;
        setTimeout(() => {
          this.copiedField = null;
        }, 2000);
      } catch (error) {
        console.error('Failed to copy to clipboard', error);
        this.$showMessage(this.$t('routes.apiClients.copyFailed'), { type: 'error' });
      }
    },
    async copyAllToClipboard() {
      const credentials = {
        client_id: this.client?.client_id,
        client_secret: this.client?.client_secret,
      };
      try {
        await navigator.clipboard.writeText(JSON.stringify(credentials, null, 2));
        this.copiedField = 'all';
        this.$showMessage(this.$t('routes.apiClients.copiedToClipboard'));
        setTimeout(() => {
          this.copiedField = null;
        }, 2000);
      } catch (error) {
        console.error('Failed to copy to clipboard', error);
        this.$showMessage(this.$t('routes.apiClients.copyFailed'), { type: 'error' });
      }
    },
    downloadAsJson() {
      const credentials = {
        client_id: this.client?.client_id,
        client_secret: this.client?.client_secret,
        name: this.client?.name,
        created_at: new Date().toISOString(),
      };

      const blob = new Blob([JSON.stringify(credentials, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `api-client-${this.client?.client_id}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      this.$showMessage(this.$t('routes.apiClients.downloadStarted'));
    },
    closeDialog() {
      this.dialog = false;
    },
  },
}
</script>

<style scoped>
.credentials-section {
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  padding: 12px;
}

.v-theme--dark .credentials-section {
  background-color: rgba(255, 255, 255, 0.05);
}

.credential-value {
  font-size: 14px;
  word-break: break-all;
  padding: 8px;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

.v-theme--dark .credential-value {
  background-color: rgba(255, 255, 255, 0.1);
}

.gap-2 {
  gap: 8px;
}
</style>
